/*!
 * PDP Offers — free gift on quantity tiers + upsell toggle (product page buy box).
 *
 * Contract (DOM, cart line properties, JS API): see the `offer_gift` / `offer_upsell` blocks in
 * sections/main-product.liquid and snippets/pdp-offer-*.liquid.
 *
 *  - PdpOffersCore: PURE functions (no DOM). In Node, `require('assets/pdp-offers.js')` returns it
 *    and nothing else runs. In the browser it is `window.PdpOffersCore`.
 *  - window.PdpOffers: browser runtime —
 *      beforeAdd(productFormEl, itemsAlreadyWritten)  called by ONE statement in product-form.js
 *      controllers (Map sectionId -> controller), getController, init, evaluateAll,
 *      reconcileCart() / whenIdle()                   gift-line cleanup after cart changes
 *  - <pdp-upsell> custom element, dynamic-checkout (Buy it now / Shop Pay) interception.
 *
 * Plain ES2019, no dependencies, no build step. Never throws into the page, never publishes pubsub
 * events, does nothing on pages/sections without offer configs (except the cart reconciliation
 * listener, which only reads /cart.js after a cart change and never changes non-gift lines).
 */
(function () {
  'use strict';

  /* ======================================================================================
   * Core — pure functions
   * ==================================================================================== */

  var GIFT_PROP = '_pdp_gift';
  var UPSELL_PROP = '_pdp_upsell';
  var WHEN_GIFTED = ['included', 'hide', 'none'];

  /** Positive safe integer from a number or a digits-only string, else null. */
  function toPosInt(v) {
    if (typeof v === 'number') return Number.isSafeInteger(v) && v > 0 ? v : null;
    if (typeof v === 'string' && /^\s*\d+\s*$/.test(v)) {
      var n = Number(v.trim());
      return Number.isSafeInteger(n) && n > 0 ? n : null;
    }
    return null;
  }

  /** Non-empty, de-duplicated list of positive ints, or null when empty / any entry is invalid. */
  function toIdList(v) {
    var arr = Array.isArray(v) ? v : v == null ? [] : [v];
    var out = [];
    for (var i = 0; i < arr.length; i++) {
      var n = toPosInt(arr[i]);
      if (n === null) return null;
      if (out.indexOf(n) === -1) out.push(n);
    }
    return out.length ? out : null;
  }

  function pick(obj, a, b) {
    return obj[a] != null ? obj[a] : obj[b];
  }

  function lineQty(item) {
    var n = Number(item && item.quantity);
    return isFinite(n) && n > 0 ? Math.floor(n) : 0;
  }

  /** Gift config (from the offer_gift JSON) -> normalized object, or null when unusable. */
  function normalizeGift(cfg) {
    if (!cfg || typeof cfg !== 'object') return null;
    var variantId = toPosInt(cfg.variantId);
    var triggerIds = toIdList(pick(cfg, 'triggerProductIds', 'triggerIds'));
    var minQty = toPosInt(pick(cfg, 'minQty', 'min'));
    var rawQty = pick(cfg, 'giftQty', 'qty');
    var giftQty = rawQty == null ? 1 : toPosInt(rawQty);
    if (!variantId || !triggerIds || !minQty || !giftQty) return null;
    return {
      id: cfg.id == null || cfg.id === '' ? String(variantId) : String(cfg.id),
      variantId: variantId,
      productId: toPosInt(cfg.productId),
      minQty: minQty,
      giftQty: giftQty,
      available: cfg.available !== false,
      triggerProductIds: triggerIds
    };
  }

  /** Upsell config (from the offer_upsell JSON) -> normalized object, or null when unusable. */
  function normalizeUpsell(cfg) {
    if (!cfg || typeof cfg !== 'object') return null;
    var variantId = toPosInt(cfg.variantId);
    var quantity = cfg.quantity == null ? 1 : toPosInt(cfg.quantity);
    if (!variantId || !quantity) return null;
    var whenGifted = String(cfg.whenGifted == null ? 'included' : cfg.whenGifted);
    if (WHEN_GIFTED.indexOf(whenGifted) === -1) whenGifted = 'included';
    return {
      id: cfg.id == null || cfg.id === '' ? String(variantId) : String(cfg.id),
      variantId: variantId,
      productId: toPosInt(cfg.productId),
      quantity: quantity,
      available: cfg.available !== false,
      whenGifted: whenGifted,
      defaultOn: cfg.defaultOn === true,
      triggerProductIds: toIdList(cfg.triggerProductIds) || []
    };
  }

  /** "trigger=1,2;min=3;qty=1" -> {triggerIds:[1,2], min:3, qty:1}; null for anything else. */
  function parseGiftProperty(str) {
    if (typeof str !== 'string' || !str.trim()) return null;
    var triggerIds = null;
    var min = null;
    var qty = null;
    var parts = str.split(';');
    for (var i = 0; i < parts.length; i++) {
      var part = parts[i].trim();
      if (!part) continue;
      var eq = part.indexOf('=');
      if (eq <= 0) return null;
      var key = part.slice(0, eq).trim();
      var val = part.slice(eq + 1).trim();
      if (key === 'trigger') {
        var raw = val.split(',');
        var ids = [];
        for (var j = 0; j < raw.length; j++) {
          var id = toPosInt(raw[j]);
          if (id === null) return null;
          if (ids.indexOf(id) === -1) ids.push(id);
        }
        triggerIds = ids;
      } else if (key === 'min') {
        min = toPosInt(val);
        if (min === null) return null;
      } else if (key === 'qty') {
        qty = toPosInt(val);
        if (qty === null) return null;
      }
      // Unknown keys are ignored (forward compatible).
    }
    if (!triggerIds || !triggerIds.length || !min || !qty) return null;
    return { triggerIds: triggerIds, min: min, qty: qty };
  }

  /** {triggerIds|triggerProductIds, min|minQty, qty|giftQty} -> "trigger=…;min=…;qty=…" (null if invalid). */
  function formatGiftProperty(rule) {
    if (!rule || typeof rule !== 'object') return null;
    var ids = toIdList(pick(rule, 'triggerIds', 'triggerProductIds'));
    var min = toPosInt(pick(rule, 'min', 'minQty'));
    var qty = toPosInt(pick(rule, 'qty', 'giftQty'));
    if (!ids || !min || !qty) return null;
    return 'trigger=' + ids.join(',') + ';min=' + min + ';qty=' + qty;
  }

  /** Normalized gifts that are available and whose minQty is reached by the selected quantity. */
  function activeGifts(gifts, selectedQty) {
    var q = toPosInt(typeof selectedQty === 'number' ? Math.floor(selectedQty) : selectedQty);
    if (!q || !Array.isArray(gifts)) return [];
    var out = [];
    for (var i = 0; i < gifts.length; i++) {
      var g = normalizeGift(gifts[i]);
      if (g && g.available && q >= g.minQty) out.push(g);
    }
    return out;
  }

  /** 'on' | 'off' | 'included' | 'hidden' for one upsell given the toggle and the active gifts. */
  function upsellState(upsell, opts) {
    var u = normalizeUpsell(upsell);
    if (!u || !u.available) return 'hidden';
    var checked = !!(opts && opts.checked);
    var gifts = opts && Array.isArray(opts.activeGifts) ? opts.activeGifts : [];
    var covered = false;
    for (var i = 0; i < gifts.length; i++) {
      if (gifts[i] && toPosInt(gifts[i].variantId) === u.variantId) {
        covered = true;
        break;
      }
    }
    if (covered && u.whenGifted === 'hide') return 'hidden';
    if (covered && u.whenGifted === 'included') return 'included';
    return checked ? 'on' : 'off';
  }

  /**
   * Add-on cart lines for the current buy-box state: gift lines first, then upsell lines.
   * opts: {gifts, upsells, selectedQty, checkedById: {upsellId: bool}, triggerProductId?}
   * An upsell whose variant is covered by an active gift is dropped for whenGifted included|hide.
   */
  function buildAddonItems(opts) {
    opts = opts || {};
    var act = activeGifts(opts.gifts || [], opts.selectedQty);
    var items = [];
    var seen = {};
    var giftTriggers = [];
    for (var i = 0; i < act.length; i++) {
      var g = act[i];
      var prop = formatGiftProperty(g);
      if (!prop) continue;
      g.triggerProductIds.forEach(function (id) {
        if (giftTriggers.indexOf(id) === -1) giftTriggers.push(id);
      });
      var dedupeKey = g.variantId + '|' + prop;
      if (seen[dedupeKey]) continue; // two identical gift blocks must not double the gift
      seen[dedupeKey] = true;
      var gp = {};
      gp[GIFT_PROP] = prop;
      items.push({ id: g.variantId, quantity: g.giftQty, properties: gp });
    }

    var checkedById = opts.checkedById && typeof opts.checkedById === 'object' ? opts.checkedById : {};
    var pageProduct = toPosInt(opts.triggerProductId);
    var upsells = Array.isArray(opts.upsells) ? opts.upsells : [];
    for (var k = 0; k < upsells.length; k++) {
      var u = normalizeUpsell(upsells[k]);
      if (!u) continue;
      var checked = checkedById[u.id] === true;
      if (upsellState(u, { checked: checked, activeGifts: act }) !== 'on') continue;
      var triggers = pageProduct ? [pageProduct] : u.triggerProductIds.length ? u.triggerProductIds : giftTriggers;
      var up = {};
      up[UPSELL_PROP] = 'trigger=' + triggers.join(',');
      items.push({ id: u.variantId, quantity: u.quantity, properties: up });
    }
    return items;
  }

  function giftPropOf(item) {
    var p = item && item.properties;
    if (!p || typeof p !== 'object' || !Object.prototype.hasOwnProperty.call(p, GIFT_PROP)) return null;
    var v = p[GIFT_PROP];
    if (v == null) return null;
    v = String(v);
    return v === '' ? null : v;
  }

  /**
   * /cart.js JSON -> [{key, quantity}] changes for /cart/change.js.
   * Only gift lines (property _pdp_gift) are ever changed:
   *  - trigger units (Σ quantity of NON-gift lines whose product_id is a trigger) < min -> remove;
   *  - otherwise, when the gift lines of one rule total more than qty -> remove the extra lines
   *    first and keep one line at qty.
   * Gift lines with an unparseable property are left alone. Idempotent.
   */
  function planCartReconciliation(cart) {
    var items = cart && Array.isArray(cart.items) ? cart.items : [];
    var nonGift = [];
    var groups = [];
    var byRule = {};
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (!item || typeof item !== 'object') continue;
      var raw = giftPropOf(item);
      if (raw === null) {
        nonGift.push(item);
        continue;
      }
      var rule = parseGiftProperty(raw);
      if (!rule || item.key == null || item.key === '') continue;
      var norm = formatGiftProperty(rule);
      if (!byRule[norm]) {
        byRule[norm] = { rule: rule, lines: [] };
        groups.push(byRule[norm]);
      }
      byRule[norm].lines.push(item);
    }

    var plan = [];
    groups.forEach(function (grp) {
      var rule = grp.rule;
      var lines = grp.lines.filter(function (l) {
        return lineQty(l) > 0;
      });
      if (!lines.length) return;
      var count = 0;
      nonGift.forEach(function (it) {
        var pid = toPosInt(it.product_id);
        if (pid && rule.triggerIds.indexOf(pid) !== -1) count += lineQty(it);
      });
      if (count < rule.min) {
        lines.forEach(function (l) {
          plan.push({ key: String(l.key), quantity: 0 });
        });
        return;
      }
      var total = lines.reduce(function (s, l) {
        return s + lineQty(l);
      }, 0);
      if (total <= rule.qty) return;
      // Keep the line already at qty, else the biggest one; drop the others (in cart order) until
      // the total fits, then trim the kept line. Never increases a line, and every prefix of the
      // plan re-plans to exactly the remaining steps (the runtime re-plans after each change).
      var keep = null;
      for (var j = 0; j < lines.length; j++) {
        if (lineQty(lines[j]) === rule.qty) {
          keep = lines[j];
          break;
        }
        if (!keep || lineQty(lines[j]) > lineQty(keep)) keep = lines[j];
      }
      var running = total;
      for (var k = 0; k < lines.length && running > rule.qty; k++) {
        if (lines[k] === keep) continue;
        plan.push({ key: String(lines[k].key), quantity: 0 });
        running -= lineQty(lines[k]);
      }
      if (running > rule.qty) plan.push({ key: String(keep.key), quantity: rule.qty });
    });
    return plan;
  }

  var Core = {
    GIFT_PROP: GIFT_PROP,
    UPSELL_PROP: UPSELL_PROP,
    normalizeGift: normalizeGift,
    normalizeUpsell: normalizeUpsell,
    parseGiftProperty: parseGiftProperty,
    formatGiftProperty: formatGiftProperty,
    activeGifts: activeGifts,
    upsellState: upsellState,
    buildAddonItems: buildAddonItems,
    planCartReconciliation: planCartReconciliation
  };

  /* ======================================================================================
   * Export: Node gets the pure core only; the browser gets the runtime too.
   * ==================================================================================== */

  var isNode =
    typeof module === 'object' &&
    module !== null &&
    typeof module.exports === 'object' &&
    typeof process === 'object' &&
    process !== null &&
    !!(process.versions && process.versions.node);
  if (isNode) {
    module.exports = Core;
    return;
  }
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  window.PdpOffersCore = Core;
  if (window.PdpOffers && window.PdpOffers.__pdpOffersLoaded) return; // script included twice

  try {
    initBrowser(window, document);
  } catch (e) {
    try {
      window.console.warn('[pdp-offers] init failed', e);
    } catch (ignore) {
      /* nothing */
    }
  }

  /* ======================================================================================
   * Browser runtime
   * ==================================================================================== */

  function initBrowser(win, doc) {
    var FORM_PREFIX = 'product-form-';
    var CART_SECTIONS = 'cart-drawer,cart-icon-bubble';
    var MAX_RECONCILE_STEPS = 20;
    var controllers = new Map();
    var jsonCache = new WeakMap();
    var appliedEls = new WeakSet();
    var listenersBound = false;

    function warn() {
      try {
        var args = Array.prototype.slice.call(arguments);
        win.console.warn.apply(win.console, ['[pdp-offers]'].concat(args));
      } catch (e) {
        /* nothing */
      }
    }

    function sel(v) {
      return String(v).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    }

    function lastPositiveInt(values) {
      var out = null;
      for (var i = 0; i < values.length; i++) {
        var n = typeof values[i] === 'string' ? parseInt(values[i], 10) : NaN;
        if (isFinite(n) && n > 0) out = n;
        else if (typeof values[i] === 'string') out = null;
      }
      return out;
    }

    function readJSON(script) {
      if (!script) return null;
      var text = script.textContent || '';
      var cached = jsonCache.get(script);
      if (cached && cached.text === text) return cached.value;
      var value = null;
      try {
        value = text.trim() ? JSON.parse(text) : null;
        if (!value || typeof value !== 'object' || Array.isArray(value)) value = null;
      } catch (e) {
        warn('ignoring invalid offer config JSON', e && e.message);
        value = null;
      }
      jsonCache.set(script, { text: text, value: value });
      return value;
    }

    function sectionOfFormId(formId) {
      return formId && formId.indexOf(FORM_PREFIX) === 0 ? formId.slice(FORM_PREFIX.length) : null;
    }

    function sectionHasOffers(sectionId) {
      var s = sel(sectionId);
      return !!(
        doc.querySelector('script[data-pdp-offer="gift"][data-section="' + s + '"]') ||
        doc.querySelector('pdp-upsell[data-section="' + s + '"]')
      );
    }

    function cartUrl(key, fallback) {
      var r = win.routes && win.routes[key];
      return (typeof r === 'string' && r ? r : fallback) + '.js';
    }

    function checkoutUrl() {
      var root = (win.routes && win.routes.root_url) || '/';
      return String(root).replace(/\/?$/, '/') + 'checkout';
    }

    /** fetch() that always returns a promise (a synchronous throw becomes a rejection). */
    function request(url, init) {
      var pending;
      try {
        pending = win.fetch(url, init);
      } catch (e) {
        return Promise.reject(e);
      }
      return Promise.resolve(pending).then(readResponse);
    }

    function postJSON(url, body) {
      return request(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(body)
      });
    }

    function getJSON(url) {
      return request(url, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
      });
    }

    function readResponse(res) {
      return res
        .json()
        .catch(function () {
          return null;
        })
        .then(function (json) {
          return { ok: !!res.ok, status: res.status, json: json };
        });
    }

    /** Main line as the product form would submit it (values from FormData). */
    function readMainLine(form) {
      var fd = new win.FormData(form);
      var idInput = form.querySelector('[name="id"]');
      var rawId = idInput ? idInput.value : fd.get('id');
      var id = toPosInt(typeof rawId === 'string' ? rawId : null);
      var quantity = lastPositiveInt(fd.getAll('quantity')) || 1;
      var properties = [];
      var sellingPlan = null;
      var hasFile = false;
      fd.forEach(function (value, key) {
        var m = /^properties\[(.*)\]$/.exec(key);
        if (!m && key !== 'selling_plan') return;
        if (typeof value !== 'string') {
          hasFile = true;
          return;
        }
        if (m) properties.push([m[1], value]);
        else if (value !== '') sellingPlan = value;
      });
      return {
        id: id,
        rawId: id ? String(rawId).trim() : null,
        quantity: quantity,
        properties: properties,
        sellingPlan: sellingPlan,
        hasFile: hasFile
      };
    }

    /* -------------------------------- controller -------------------------------- */

    function Controller(sectionId) {
      this.sectionId = sectionId;
      this.formId = FORM_PREFIX + sectionId;
      this.choices = {}; // upsell id -> the shopper's own toggle choice
      this.busy = false;
    }

    Controller.prototype.form = function () {
      var f = doc.getElementById(this.formId);
      return f && f.tagName === 'FORM' ? f : null;
    };

    Controller.prototype.gifts = function () {
      var out = [];
      var nodes = doc.querySelectorAll('script[data-pdp-offer="gift"][data-section="' + sel(this.sectionId) + '"]');
      for (var i = 0; i < nodes.length; i++) {
        var g = normalizeGift(readJSON(nodes[i]));
        if (g) out.push(g);
      }
      return out;
    };

    Controller.prototype.upsells = function () {
      var out = [];
      var els = doc.querySelectorAll('pdp-upsell[data-section="' + sel(this.sectionId) + '"]');
      for (var i = 0; i < els.length; i++) {
        var el = els[i];
        var cfg = normalizeUpsell(readJSON(el.querySelector('script[data-pdp-offer="upsell"]')));
        var input = el.querySelector('[data-pdp-upsell-toggle]');
        if (cfg && !Object.prototype.hasOwnProperty.call(this.choices, cfg.id)) {
          // Start from the server default (default_on), NOT from a browser-restored checkbox state.
          this.choices[cfg.id] = !!((input && input.defaultChecked) || cfg.defaultOn);
        }
        out.push({ el: el, cfg: cfg, input: input });
      }
      return out;
    };

    /** Selected quantity for the UI state: last enabled quantity control of this form (radios or number). */
    Controller.prototype.selectedQty = function () {
      var form = this.form();
      var all = doc.querySelectorAll('input[name="quantity"]');
      var val = null;
      for (var i = 0; i < all.length; i++) {
        var el = all[i];
        var associated = form ? el.form === form : el.getAttribute('form') === this.formId;
        if (!associated || el.disabled) continue;
        if (el.type === 'radio' || el.type === 'checkbox') {
          if (el.checked) val = el.value;
        } else {
          val = el.value;
        }
      }
      return lastPositiveInt(val == null ? [] : [String(val)]) || 1;
    };

    Controller.prototype.pageProductId = function () {
      var form = this.form();
      var input = form && form.querySelector('input[name="product-id"]');
      return input ? input.value : null;
    };

    /** Pick up the visible toggle position (only for rows we already rendered in on/off). */
    Controller.prototype.syncChoices = function (list) {
      var self = this;
      list.forEach(function (u) {
        if (!u.cfg || !u.input || u.input.disabled || !appliedEls.has(u.el)) return;
        var st = u.el.getAttribute('data-state');
        if (st === 'on' || st === 'off') self.choices[u.cfg.id] = !!u.input.checked;
      });
    };

    Controller.prototype.evaluate = function () {
      var self = this;
      var act = activeGifts(this.gifts(), this.selectedQty());
      this.upsells().forEach(function (u) {
        var state = u.cfg
          ? upsellState(u.cfg, { checked: self.choices[u.cfg.id] === true, activeGifts: act })
          : 'hidden';
        applyState(u, state, u.cfg ? self.choices[u.cfg.id] === true : false);
      });
      return act;
    };

    Controller.prototype.computeAddons = function (qty) {
      var self = this;
      var list = this.upsells();
      this.syncChoices(list);
      var checkedById = {};
      var cfgs = [];
      list.forEach(function (u) {
        if (!u.cfg) return;
        cfgs.push(u.cfg);
        checkedById[u.cfg.id] = self.choices[u.cfg.id] === true;
      });
      return buildAddonItems({
        gifts: this.gifts(),
        upsells: cfgs,
        selectedQty: qty != null ? qty : this.selectedQty(),
        checkedById: checkedById,
        triggerProductId: this.pageProductId()
      });
    };

    /** Buy it now with add-ons: POST /cart/add.js (main + add-ons) then go to checkout. */
    Controller.prototype.buyNow = function (line, addons, productFormEl, wrapper) {
      if (this.busy) return null;
      var self = this;
      this.busy = true;
      var main = { id: line.id, quantity: line.quantity };
      if (line.properties.length) {
        main.properties = {};
        line.properties.forEach(function (p) {
          main.properties[p[0]] = p[1];
        });
      }
      if (line.sellingPlan) main.selling_plan = toPosInt(line.sellingPlan) || line.sellingPlan;
      var items = [main].concat(
        addons.map(function (a) {
          return { id: a.id, quantity: a.quantity, properties: a.properties };
        })
      );
      setBusy(wrapper, true);
      var done = function (message) {
        self.busy = false;
        setBusy(wrapper, false);
        showError(productFormEl, message);
      };
      return postJSON(cartUrl('cart_add_url', '/cart/add'), { items: items })
        .then(function (res) {
          if (!res.ok || !res.json || res.json.status) {
            var j = res.json || {};
            done(j.description || j.message || (win.cartStrings && win.cartStrings.error) || 'Error');
            return;
          }
          api._nav.assign(checkoutUrl());
        })
        .catch(function (e) {
          warn('buy-now add failed', e);
          done((win.cartStrings && win.cartStrings.error) || 'Error');
        });
    };

    function setBusy(wrapper, on) {
      try {
        if (!wrapper) return;
        wrapper.classList.toggle('pdp-offers-busy', on);
        if (on) wrapper.setAttribute('aria-busy', 'true');
        else wrapper.removeAttribute('aria-busy');
      } catch (e) {
        /* nothing */
      }
    }

    function showError(productFormEl, message) {
      try {
        if (productFormEl && typeof productFormEl.handleErrorMessage === 'function') {
          productFormEl.handleErrorMessage(message);
        }
      } catch (e) {
        warn(e);
      }
    }

    function applyState(u, state, choice) {
      var el = u.el;
      if (el.getAttribute('data-state') !== state) el.setAttribute('data-state', state);
      var hide = state === 'hidden';
      if (el.hasAttribute('hidden') !== hide) {
        if (hide) el.setAttribute('hidden', '');
        else el.removeAttribute('hidden');
      }
      var input = u.input;
      if (input) {
        var checked = state === 'on' || state === 'included' ? true : state === 'off' ? false : choice;
        if (input.checked !== checked) input.checked = checked;
        var disabled = state === 'included';
        if (input.disabled !== disabled) input.disabled = disabled;
      }
      appliedEls.add(el);
    }

    function ensureController(sectionId) {
      if (!sectionId) return null;
      var c = controllers.get(sectionId);
      if (c) return c;
      if (!sectionHasOffers(sectionId)) return null;
      c = new Controller(sectionId);
      controllers.set(sectionId, c);
      bindGlobalListeners();
      return c;
    }

    function safeEvaluate(c) {
      try {
        c.evaluate();
      } catch (e) {
        warn('evaluate failed', e);
      }
    }

    function evaluateAll() {
      controllers.forEach(safeEvaluate);
    }

    function scan() {
      try {
        var nodes = doc.querySelectorAll('script[data-pdp-offer]');
        var seen = {};
        for (var i = 0; i < nodes.length; i++) {
          var s = nodes[i].getAttribute('data-section');
          if (!s) {
            var host = nodes[i].closest('pdp-upsell');
            s = host && host.getAttribute('data-section');
          }
          if (s && !seen[s]) {
            seen[s] = true;
            ensureController(s);
          }
        }
        evaluateAll();
      } catch (e) {
        warn('scan failed', e);
      }
    }

    /* ------------------------------ product form hook ------------------------------ */

    /**
     * Called by product-form.js right before it builds the FormData. Returns the new value of its
     * "items already written" flag. With no active add-on it returns the input and touches nothing.
     */
    function beforeAdd(productFormEl, itemsAlreadyWritten) {
      var written = itemsAlreadyWritten;
      try {
        if (!productFormEl || productFormEl.nodeType !== 1) return written;
        var form =
          productFormEl.form && productFormEl.form.tagName === 'FORM'
            ? productFormEl.form
            : productFormEl.querySelector('form');
        if (!form) return written;
        var sectionId = productFormEl.getAttribute('data-section') || sectionOfFormId(form.getAttribute('id'));
        var ctrl = ensureController(sectionId);
        if (!ctrl) return written;
        var variantInputs = productFormEl.variantInputs || form.querySelector('.product-form__variants');
        if (!variantInputs) return written;

        var line = readMainLine(form);
        if (!written && line.hasFile) {
          // A File can't be copied into items[0][properties]; keep today's request untouched.
          warn('file line-item property present: add-ons skipped');
          return written;
        }
        var addons = ctrl.computeAddons(line.quantity);
        if (!addons.length) return written;

        var frag = doc.createDocumentFragment();
        var next;
        if (!written) {
          if (!line.id) {
            warn('no variant id in the product form: add-ons skipped');
            return written;
          }
          appendHidden(frag, 'items[0][id]', line.rawId);
          appendHidden(frag, 'items[0][quantity]', String(line.quantity));
          line.properties.forEach(function (p) {
            appendHidden(frag, 'items[0][properties][' + p[0] + ']', p[1]);
          });
          if (line.sellingPlan) appendHidden(frag, 'items[0][selling_plan]', line.sellingPlan);
          next = 1;
        } else {
          next = nextItemIndex(variantInputs);
          if (next === 0) return written; // nothing written after all: leave the request alone
        }
        addons.forEach(function (a, i) {
          var n = next + i;
          appendHidden(frag, 'items[' + n + '][id]', String(a.id));
          appendHidden(frag, 'items[' + n + '][quantity]', String(a.quantity));
          Object.keys(a.properties || {}).forEach(function (k) {
            appendHidden(frag, 'items[' + n + '][properties][' + k + ']', String(a.properties[k]));
          });
        });
        variantInputs.appendChild(frag); // single DOM write: all-or-nothing
        return true;
      } catch (e) {
        warn('beforeAdd failed: adding without add-ons', e);
        return written;
      }
    }

    function appendHidden(parent, name, value) {
      var input = doc.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      parent.appendChild(input);
    }

    function nextItemIndex(container) {
      var max = -1;
      var inputs = container.querySelectorAll('[name]'); // no '[' inside selectors: some engines choke on it
      for (var i = 0; i < inputs.length; i++) {
        var m = /^items\[(\d+)\]/.exec(inputs[i].getAttribute('name') || '');
        if (m) max = Math.max(max, parseInt(m[1], 10));
      }
      return max + 1;
    }

    /* ------------------------------ DOM event handlers ------------------------------ */

    function onFieldEvent(ev) {
      try {
        var t = ev.target;
        if (!t || t.nodeType !== 1) return;
        if (t.hasAttribute('data-pdp-upsell-toggle')) {
          var host = t.closest('pdp-upsell');
          var c = host && ensureController(host.getAttribute('data-section'));
          if (!c) return;
          var cfg = normalizeUpsell(readJSON(host.querySelector('script[data-pdp-offer="upsell"]')));
          var st = host.getAttribute('data-state');
          if (cfg && !t.disabled && st !== 'included' && st !== 'hidden') c.choices[cfg.id] = !!t.checked;
          c.evaluate();
          return;
        }
        if (t.tagName === 'INPUT' && t.name === 'quantity') {
          var formId = t.getAttribute('form') || (t.form && t.form.getAttribute('id')) || '';
          var c2 = controllers.get(sectionOfFormId(formId));
          if (c2) c2.evaluate();
        }
      } catch (e) {
        warn('state update failed', e);
      }
    }

    /**
     * The upsell row usually sits INSIDE the product <form> (placement "under_atc"), and browsers submit a
     * form when Enter is pressed on a focused checkbox (implicit submission) — that would be an Add to cart.
     * Enter toggles the switch instead (like Space); the keypress is swallowed too.
     */
    function onToggleKey(ev) {
      try {
        if (ev.key !== 'Enter') return;
        var t = ev.target;
        if (!t || t.nodeType !== 1 || !t.hasAttribute || !t.hasAttribute('data-pdp-upsell-toggle')) return;
        if (ev.cancelable) ev.preventDefault();
        if (ev.type === 'keydown' && !ev.repeat && !t.disabled) t.click();
      } catch (e) {
        warn('switch key failed', e);
      }
    }

    var ACTIVATION_KEYS = { Enter: true, ' ': true, Spacebar: true };

    /** Capture-phase guard on the dynamic checkout buttons (Shop Pay / Buy it now / more options). */
    function onPaymentEvent(ev) {
      try {
        if (!controllers.size) return;
        if (ev.type === 'keydown' && !ACTIVATION_KEYS[ev.key]) return;
        var t = ev.target;
        if (!t || t.nodeType !== 1 || !t.closest) return;
        var wrapper = t.closest('.shopify-payment-button');
        if (!wrapper) return;
        var form = wrapper.closest('form');
        var productFormEl = wrapper.closest('product-form');
        if (!form) return;
        var sectionId =
          (productFormEl && productFormEl.getAttribute('data-section')) || sectionOfFormId(form.getAttribute('id'));
        var c = sectionId ? controllers.get(sectionId) : null;
        if (!c) return;
        var line = readMainLine(form);
        if (!line.id || line.hasFile) return;
        var addons = c.computeAddons(line.quantity);
        if (!addons.length) return; // nothing to add: native dynamic checkout, untouched
        // touchstart stays passive and is only stopped: preventing it would cancel the click.
        if (ev.type !== 'touchstart' && ev.cancelable) ev.preventDefault();
        ev.stopImmediatePropagation();
        if (ev.type === 'click' || ev.type === 'keydown') c.buyNow(line, addons, productFormEl, wrapper);
      } catch (e) {
        warn('dynamic checkout guard failed', e);
      }
    }

    function bindGlobalListeners() {
      if (listenersBound) return;
      listenersBound = true;
      doc.addEventListener('change', onFieldEvent, true);
      doc.addEventListener('input', onFieldEvent, true);
      doc.addEventListener('keydown', onToggleKey, true);
      doc.addEventListener('keypress', onToggleKey, true);
      ['pointerdown', 'mousedown', 'keydown', 'click'].forEach(function (type) {
        doc.addEventListener(type, onPaymentEvent, true);
      });
      doc.addEventListener('touchstart', onPaymentEvent, { capture: true, passive: true });
      doc.addEventListener('DOMContentLoaded', evaluateAll);
      win.addEventListener('load', evaluateAll);
      win.addEventListener('pageshow', function () {
        controllers.forEach(function (c) {
          c.busy = false;
        });
        evaluateAll();
      });
    }

    /* ------------------------------ cart reconciliation ------------------------------ */

    var reconcileRunning = null;
    var reconcileAgain = false;

    function reconcileCart() {
      if (reconcileRunning) {
        reconcileAgain = true;
        return reconcileRunning;
      }
      var p = runReconcile()
        .catch(function (e) {
          warn('cart reconciliation failed', e);
        })
        .then(function () {
          reconcileRunning = null;
          if (reconcileAgain) {
            reconcileAgain = false;
            return reconcileCart();
          }
          return undefined;
        });
      reconcileRunning = p;
      return p;
    }

    async function runReconcile() {
      var res = await getJSON(cartUrl('cart_url', '/cart'));
      if (!res.ok || !res.json) return;
      var plan = planCartReconciliation(res.json);
      var last = null;
      var prev = null;
      var steps = 0;
      while (plan.length && steps < MAX_RECONCILE_STEPS) {
        var step = plan[0];
        if (prev && prev.key === step.key && prev.quantity === step.quantity) {
          warn('cart change had no effect; stopping', step);
          break;
        }
        steps++;
        var r;
        try {
          r = await postJSON(cartUrl('cart_change_url', '/cart/change'), {
            id: step.key,
            quantity: step.quantity,
            sections: CART_SECTIONS,
            sections_url: win.location.pathname
          });
        } catch (e) {
          warn('cart change failed', e);
          break; // still refresh the UI for the steps that went through
        }
        if (!r.ok || !r.json) {
          warn('cart change rejected', r.status, r.json);
          break;
        }
        last = r.json;
        prev = step;
        plan = Array.isArray(last.items) ? planCartReconciliation(last) : plan.slice(1);
      }
      if (last) renderAfterReconcile(last, plan.length === 0);
    }

    function reloadAllowed() {
      try {
        var key = 'pdpOffersReloadAt';
        var now = Date.now();
        var prev = Number(win.sessionStorage.getItem(key)) || 0;
        if (now - prev < 10000) return false;
        win.sessionStorage.setItem(key, String(now));
      } catch (e) {
        /* storage unavailable: the "clean plan" condition alone prevents loops */
      }
      return true;
    }

    function renderAfterReconcile(json, clean) {
      try {
        if (doc.querySelector('cart-items')) {
          // Cart page (<cart-drawer-items> is a different tag): its markup can't be patched from here.
          if (clean && reloadAllowed()) api._nav.reload();
          return;
        }
        var drawer = doc.querySelector('cart-drawer');
        if (!drawer || !json.sections || !json.sections['cart-drawer']) return;
        var render = function () {
          try {
            if (typeof drawer.renderContents !== 'function') return;
            drawer.classList.toggle('is-empty', json.item_count === 0);
            drawer.renderContents(json);
          } catch (e) {
            warn('drawer refresh failed', e);
          }
        };
        if (typeof drawer.renderContents === 'function') render();
        else if (win.customElements && win.customElements.whenDefined) {
          win.customElements.whenDefined('cart-drawer').then(render, function () {});
        }
      } catch (e) {
        warn('cart refresh failed', e);
      }
    }

    function subscribeToCartUpdates() {
      try {
        if (typeof subscribe !== 'function') return; // global from pubsub.js
        var name =
          (typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS && PUB_SUB_EVENTS.cartUpdate) || 'cart-update';
        subscribe(name, function () {
          try {
            reconcileCart();
          } catch (e) {
            warn(e);
          }
        });
      } catch (e) {
        warn('could not subscribe to cart updates', e);
      }
    }

    /* ------------------------------ public API + boot ------------------------------ */

    var api = {
      __pdpOffersLoaded: true,
      version: '1.0.0',
      beforeAdd: beforeAdd,
      controllers: controllers,
      getController: ensureController,
      init: scan,
      evaluateAll: evaluateAll,
      reconcileCart: reconcileCart,
      whenIdle: function () {
        return reconcileRunning || Promise.resolve();
      },
      // Navigation seam (tests replace these; window.location itself is unforgeable).
      _nav: {
        assign: function (url) {
          win.location.href = url;
        },
        reload: function () {
          win.location.reload();
        }
      }
    };
    win.PdpOffers = api;

    if (win.customElements && !win.customElements.get('pdp-upsell')) {
      win.customElements.define(
        'pdp-upsell',
        class PdpUpsell extends win.HTMLElement {
          connectedCallback() {
            try {
              var c = ensureController(this.getAttribute('data-section'));
              if (c) c.evaluate();
            } catch (e) {
              warn('pdp-upsell connect failed', e);
            }
          }
        }
      );
    }

    scan();
    subscribeToCartUpdates();

    if (doc.documentElement.getAttribute('data-pdp-gift-in-cart') === 'true') {
      var runOnce = function () {
        reconcileCart();
      };
      if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', runOnce, { once: true });
      else win.setTimeout(runOnce, 0);
    }
  }
})();
